import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Persist a single string filter value in `useSearchParams`. Empty strings
 * delete the key from the URL so deep-links don't carry around blank values.
 * History is replaced (not pushed) so typing in a search box doesn't pollute
 * the browser back stack with one entry per keystroke.
 */
export function useFilterParam(
  key: string,
): readonly [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? "";
  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next) sp.set(key, next);
          else sp.delete(key);
          return sp;
        },
        { replace: true },
      );
    },
    [key, setSearchParams],
  );
  return [value, setValue] as const;
}

/**
 * Bulk reset helper — deletes every supplied key from the URL search params
 * in a single history replace so "Clear filters" doesn't trigger N renders.
 */
export function useClearFilterParams(keys: readonly string[]) {
  const [, setSearchParams] = useSearchParams();
  return useCallback(() => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        for (const k of keys) sp.delete(k);
        return sp;
      },
      { replace: true },
    );
  }, [keys, setSearchParams]);
}

/**
 * Inclusive ISO yyyy-mm-dd range check used by all list filter bars. Rows
 * without a date are excluded the moment any bound is set so "show me docs
 * in this range" never sneaks undated rows into the result.
 */
export function isWithinDateRange(
  rowDate: string | null | undefined,
  from: string,
  to: string,
): boolean {
  if (!from && !to) return true;
  if (!rowDate) return false;
  const d = rowDate.slice(0, 10);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}
